import React, { useState } from 'react';

import cx from 'classnames';
import JsFileDownloader from 'js-file-downloader';
import { isEmpty } from 'ramda';
import { Link } from 'react-router-dom';

import { collectGalleryPhotos } from 'components/Pages/PropertyPortal/functions';
import Button from 'components/shared/Button';
import View from 'components/shared/FileDropZone/View';
import Icon from 'components/shared/Icon';
import Download2 from 'components/shared/Icon/icons/Download2';
import LinkComponent from 'components/shared/Icon/icons/Link';
import Visibility from 'components/shared/Icon/icons/Visibility';
import MapBlock from 'components/shared/MapBlock';
import Modal from 'components/shared/Modal';
import tableStyles from 'components/shared/Table/index.module.css';
import { useSnackbar } from 'hooks/useSnackbar';
import { ASSET_SIZE_TYPES, getAssetSrcFromId } from 'utils/asset';
import parseVideoUrl from 'utils/parseVideoUrl';

import Slider from '../Slider';
import Contacts from './Contacts';
import s from './index.module.css';

const COUNT_TABS = 7;

const INTERNAL_NOTES_TAB = 'Internal Notes';
const RATES_INFO_SHEETS = 'Rates and Info Sheets';
const LIVE_AVAILABILITY_TAB = 'Live Availability';
const ROOMS_TAB = 'Rooms';
const GOOGLE_MAPS_TAB = 'Google Maps';
const VIDEOS_TAB = 'Videos & Webinars';
const PHOTOS_TAB = 'Photos';
const EMPTY_BLOCK = '';

const TABS = [
  // INTERNAL_NOTES_TAB,
  RATES_INFO_SHEETS,
  LIVE_AVAILABILITY_TAB,
  ROOMS_TAB,
  GOOGLE_MAPS_TAB,
  VIDEOS_TAB,
  PHOTOS_TAB,
  EMPTY_BLOCK,
];

const RULES = {
  [RATES_INFO_SHEETS]: (data) => data.documents.length > 0,
  [LIVE_AVAILABILITY_TAB]: (data) => data.bookingUrl || data.bookingDescription,
  [ROOMS_TAB]: (data) => data.rooms.length > 0,
  [VIDEOS_TAB]: (data) => data.videos?.length > 0 || data.webinars?.length > 0,
  [GOOGLE_MAPS_TAB]: (data) => data.latitude && data.longitude,
  [PHOTOS_TAB]: () => true,
  [INTERNAL_NOTES_TAB]: () => false,
};

const Content = ({ title, simple, center, children }) => {
  return (
    <div className={s.content}>
      {title !== undefined && (
        <div
          className={cx(tableStyles.cell, tableStyles.secondaryCell, s.contactsTitle)}
          children={title || <>&nbsp;</>}
        />
      )}
      <div
        className={cx(s.contentInner, { [s.contentPadding]: !simple, [s.contentCenter]: center })}
        children={children}
      />
    </div>
  );
};

const InnerTable = ({ title, data, actions }) => {
  const tdClassName = cx(tableStyles.cell, tableStyles.cellTd, s.padding);

  return (
    <Content simple>
      <table className={cx(tableStyles.table, tableStyles.tableMinWidthAuto)}>
        <thead className={tableStyles.thead}>
          <tr>
            <th className={cx(tableStyles.cell, tableStyles.secondaryCell, s.contactsTitle)}>{title}</th>
            <th className={cx(tableStyles.cell, tableStyles.secondaryCell, tableStyles.cellActions, s.actionsCell)}>
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((single) => {
            return (
              <tr key={single.id}>
                <td className={tdClassName}>{single.name}</td>
                <td className={cx(tdClassName, s.actionsCell)}>
                  <div className={s.actions}>
                    {actions.map(([icon, action, isLink, download], i) => {
                      const ico = (
                        <Icon
                          component={icon}
                          // fill="#3F3C43"
                          // stroke="#3F3C43"
                          width="1rem"
                          height="0.5rem"
                          onClick={!isLink ? action(single) : undefined}
                          className={s.action}
                        />
                      );

                      if (!isLink) {
                        return <div className={s.actionWrapper} children={ico} />;
                      }

                      const to = action(single);

                      if (/:\/\//.test(to)) {
                        return (
                          <a
                            href={to}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={s.actionWrapper}
                            children={ico}
                            download={download && download(single)}
                          />
                        );
                      }

                      return <Link key={i} to={to} className={s.actionWrapper} children={ico} />;
                    })}
                  </div>
                </td>
              </tr>
            );
          })}
          {data.length < COUNT_TABS &&
            new Array(COUNT_TABS - data.length - 1).fill(null).map((_, i) => (
              <tr key={i} className={s.emptyRow}>
                <td className={tdClassName}>&nbsp;</td>
                <td className={cx(tdClassName, s.actionsCell)}>&nbsp;</td>
              </tr>
            ))}
        </tbody>
      </table>
    </Content>
  );
};

const Inner = ({ active, data, onOpenPreview }) => {
  const [modal, setModal] = useState({});
  const onSnackbar = useSnackbar();

  switch (active) {
    case INTERNAL_NOTES_TAB: {
      return <Content title="Internal Notes" />;
    }

    case GOOGLE_MAPS_TAB: {
      return (
        <Content title={data.address} simple>
          <div className={s.mapWrapper}>
            <MapBlock coords={{ lat: data.latitude, lng: data.longitude }} containerStyle={{ height: '100%' }} />
          </div>
        </Content>
      );
    }

    case ROOMS_TAB: {
      return (
        <InnerTable
          title="Room Type"
          data={data.rooms}
          actions={[
            [
              Visibility,
              () => () => {
                onOpenPreview(data.pageId, { initialAnchorSection: ROOMS_TAB });
              },
            ],
          ]}
        />
      );
    }

    case VIDEOS_TAB: {
      const videos = data.videos ?? [];
      const webinars = data.webinars ?? [];
      const resultData = [...videos, ...webinars];

      return (
        <>
          <InnerTable
            title="Videos & Webinars"
            data={resultData}
            actions={[
              [Visibility, (single) => () => setModal({ type: VIDEOS_TAB, url: single.link }), false],
              [LinkComponent, (single) => single.link, true],
            ]}
          />
          <Modal isOpen={modal.type === VIDEOS_TAB} toClose={() => setModal({})}>
            {modal.type === VIDEOS_TAB && (
              <iframe
                width="560"
                height="315"
                src={parseVideoUrl(modal.url)}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
          </Modal>
        </>
      );
    }

    case LIVE_AVAILABILITY_TAB: {
      return (
        <Content title="">
          <div className={s.live}>
            <div>
              <div style={{ marginBottom: '1rem' }}>{data.bookingDescription}</div>
              {data.bookingUrl && (
                <Button href={data.bookingUrl} target="_blank" rel="noopener noreferrer">
                  External Live Availability
                </Button>
              )}
            </div>
          </div>
        </Content>
      );
    }

    case RATES_INFO_SHEETS: {
      return (
        <>
          <InnerTable
            title="File Name"
            data={data.documents}
            actions={[
              [Visibility, (single) => () => setModal({ type: RATES_INFO_SHEETS, data: single })],
              [
                Download2,
                (single) => async () => {
                  try {
                    await new JsFileDownloader({ url: single.url, filename: single.filename });
                    onSnackbar(`The file '${single.filename}' was downloaded.`);
                  } catch {
                    onSnackbar(`'${single.filename}' couldn't be downloaded. Please try again.`, true);
                  }
                },
              ],
            ]}
          />
          <Modal isOpen={modal.type === RATES_INFO_SHEETS} toClose={() => setModal({})} size="auto">
            {modal.type === RATES_INFO_SHEETS && (
              <View
                url={modal.data.url}
                type={modal.data.mimeType}
                filename={modal.data.filename}
                dynamic
                fileAccId={modal.data?.accountId}
                assetStorageType={modal.data.storageType}
              />
            )}
          </Modal>
        </>
      );
    }

    case PHOTOS_TAB: {
      const images = collectGalleryPhotos(data).map((id) => ({
        type: 'media',
        url: getAssetSrcFromId(id, ASSET_SIZE_TYPES.FULL),
      }));

      return (
        <Content simple>
          <Slider images={images} />
        </Content>
      );
    }

    default: {
      return (
        <Content>
          <div style={{ fontSize: '16px', lineHeight: '24px' }}>Unknown type: {active}</div>
        </Content>
      );
    }
  }
};

const Row = ({ data, onOpenPreview }) => {
  const [active, setActive] = useState(TABS[0]);
  const isNoData = isEmpty(data.emergencyContacts) && isEmpty(data.commonContacts);

  return (
    <tr className={s.row}>
      <td className={s.contactsCell}>
        <Content title="Contacts">
          {isNoData && 'No contacts on file.'}
          {!isNoData && <Contacts title="Emergency Contacts" contacts={data.emergencyContacts} />}
          {!isNoData && <Contacts title="Office Contacts" contacts={data.commonContacts} />}
        </Content>
      </td>
      <td colSpan={4}>
        <div className={s.dataCell}>
          <div className={s.tabs}>
            {TABS.map((name, i) => (
              <div
                key={name || i}
                onClick={() => name && setActive(name)}
                className={cx(s.tab, { [s.tabActive]: active === name, [s.tabInactive]: !name })}
                children={name || <>&nbsp;</>}
              />
            ))}
          </div>
          <div style={{ flexGrow: 1, position: 'relative' }}>
            <Selector value={RULES[active].call(null, data)}>
              <Inner active={active} data={data} onOpenPreview={onOpenPreview} />
              <Content center>We don't have any content here yet.</Content>
            </Selector>
          </div>
        </div>
      </td>
    </tr>
  );
};

function Selector({ value, children }) {
  return value ? children[0] : children[1];
}

export default Row;
